import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ArrowLeft, Trophy, Gift, Loader2 } from "lucide-react";
import Seo from "../components/Seo";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const RANK_TONES = { 1: "#FFB020", 2: "#C0C6CF", 3: "#CD7F32" };

/** ReferralLeaderboardPage — public shout-out board of top EVIQ referrers.
 *  Anonymized handles (or opt-in display names) only — no emails exposed. */
export default function ReferralLeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/referral/leaderboard?limit=50`)
      .then((r) => (r.ok ? r.json() : { rows: [] }))
      .then((d) => setRows(d.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="referral-leaderboard-page">
      <Seo
        title="EVIQ Referral Leaderboard — top battery-intelligence ambassadors"
        description="Public shout-out board of EVIQ's top referrers. Share your link, help friends know their EV battery, and climb the board."
        canonical="https://eviq.pro/referrers"
      />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <Link
          to="/"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5 mb-6"
          data-testid="referral-leaderboard-back"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={2.4} />
          EVIQ
        </Link>

        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-2 inline-flex items-center gap-2">
            <Users className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
            Referral Leaderboard
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-white font-semibold leading-tight">
            The people <span className="brand-text-gradient">spreading battery intelligence.</span>
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mt-3 max-w-xl">
            Every referral here helped a friend truly know their EV's battery.
            Handles are anonymized unless the referrer chose a shout-out name.
            Subscribers earn a free month per referral (1/quarter, up to 4/year).
          </p>
        </div>

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-zinc-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.2} />
            Loading the board…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-zinc-400 text-sm" data-testid="referral-leaderboard-empty">
            No referrals yet — the top spot is wide open. Grab your link in the
            app under Plans &amp; pricing and be the first name here.
          </div>
        ) : (
          <div className="space-y-2" data-testid="referral-leaderboard-rows">
            {rows.map((r) => {
              const tone = RANK_TONES[r.rank] || "rgba(255,255,255,0.35)";
              return (
                <div
                  key={r.rank}
                  className="rounded-xl border p-3.5 flex items-center gap-3"
                  style={{
                    borderColor: r.rank <= 3 ? `${tone}55` : "rgba(255,255,255,0.08)",
                    background: r.rank <= 3 ? `${tone}0D` : "rgba(255,255,255,0.02)",
                  }}
                  data-testid={`referral-leaderboard-row-${r.rank}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 font-display text-sm font-semibold"
                    style={{ borderColor: `${tone}66`, color: tone, background: `${tone}12` }}
                  >
                    {r.rank <= 3 ? <Trophy className="w-4 h-4" strokeWidth={2.4} /> : `#${r.rank}`}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white font-semibold truncate" data-testid={`referral-leaderboard-name-${r.rank}`}>
                      {r.name}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                      {r.referrals} referral{r.referrals === 1 ? "" : "s"}
                      {r.months_earned > 0 ? ` · ${r.months_earned} free month${r.months_earned === 1 ? "" : "s"} earned` : ""}
                    </div>
                  </div>
                  {r.rank === 1 && (
                    <span className="shrink-0 font-mono text-[8px] uppercase tracking-[0.14em] rounded-full px-2 py-1 border border-[#FFB020]/50 text-[#FFB020] bg-[#FFB020]/[0.10]">
                      Top ambassador
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div
          className="rounded-xl border p-4 mt-6 flex items-start gap-3"
          style={{ borderColor: "rgba(0,229,255,0.28)", background: "rgba(0,229,255,0.05)" }}
          data-testid="referral-leaderboard-cta"
        >
          <Gift className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
          <div>
            <div className="text-sm text-white font-semibold">Want your name up here?</div>
            <p className="font-mono text-[10px] text-zinc-400 mt-1 leading-relaxed">
              Subscribers get a personal referral link in the app — each friend
              who joins earns you a free month.
            </p>
            <Link
              to="/app"
              className="inline-block mt-2 rounded-full brand-gradient text-black px-4 py-1.5 text-[11px] font-semibold"
              data-testid="referral-leaderboard-open-app"
            >
              Open EVIQ
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
